// src/pages/About.jsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export default function About() {
  const [selectedBlog, setSelectedBlog] = useState(null);

  const blogs = [
    {
      id: 1,
      title: 'Modern Frontend: React 18 & Concurrent Mode',
      summary: 'Dive into React 18’s concurrent features for smoother UIs.',
      content: (
        <>
          <p>React 18 introduced the much-anticipated Concurrent Mode...</p>
          <p>Another key feature is the new <strong>startTransition</strong> API...</p>
          <p>With these enhancements, React applications can deliver a more responsive experience...</p>
        </>
      ),
    },
    {
      id: 2,
      title: 'Backend Evolution with Node.js 20',
      summary: 'Discover performance boosts and security in Node.js 20.',
      content: (
        <>
          <p>Node.js 20 upgrades the V8 engine, delivering significant performance improvements...</p>
          <p>Native <strong>fetch</strong> support removes the need for external libraries...</p>
          <p>Security features like the Web Crypto API and Permission API have matured...</p>
        </>
      ),
    },
    {
      id: 3,
      title: 'Styling at Scale: Tailwind CSS 3.x',
      summary: 'Leverage Tailwind’s JIT for lightning-fast, utility-first styling.',
      content: (
        <>
          <p>Tailwind CSS 3.x comes with Just-In-Time (JIT) mode by default...</p>
          <p>With features like <strong>arbitrary value support</strong>...</p>
          <p>Dark mode, container queries, and animation utilities make responsive design simple...</p>
        </>
      ),
    },
    {
      id: 4,
      title: 'Serverless on AWS Lambda',
      summary: 'Build scalable, cost-effective apps with AWS Lambda.',
      content: (
        <>
          <p>AWS Lambda lets you run code without provisioning servers...</p>
          <p>Use <strong>Provisionsed Concurrency</strong> to eliminate cold starts...</p>
          <p>Best practices include using IaC with CloudFormation or Terraform...</p>
        </>
      ),
    },
    {
      id: 5,
      title: 'GraphQL vs REST',
      summary: 'Pros and cons of GraphQL and REST for modern APIs.',
      content: (
        <>
          <p>REST has been the standard for decades, offering simplicity...</p>
          <p>GraphQL allows clients to request exactly what they need...</p>
          <p>Consider factors like team expertise when choosing between REST and GraphQL...</p>
        </>
      ),
    },
    {
      id: 6,
      title: 'Containers & Kubernetes',
      summary: 'Deploy and scale apps with Docker and K8s orchestration.',
      content: (
        <>
          <p>Docker containerizes applications and their dependencies...</p>
          <p>Kubernetes automates deployment, scaling, and management...</p>
          <p>Implement health checks, rolling updates, and autoscaling...</p>
        </>
      ),
    },
    {
      id: 7,
      title: 'CI/CD with GitHub Actions',
      summary: 'Automate builds, tests, and deployments effectively.',
      content: (
        <>
          <p>GitHub Actions provides native CI/CD in GitHub repos...</p>
          <p>Use matrix strategies to test across multiple versions...</p>
          <p>Secure your pipelines by using encrypted secrets...</p>
        </>
      ),
    },
    {
      id: 8,
      title: 'Microservices vs Monoliths',
      summary: 'Architectural trade-offs when scaling your app.',
      content: (
        <>
          <p>Monolithic architectures bundle all services into a single unit...</p>
          <p>Microservices break functionality into independent services...</p>
          <p>Adopt a modular monolith first, then extract services as needed...</p>
        </>
      ),
    },
    {
      id: 9,
      title: 'Observability with Prometheus',
      summary: 'Gain insights with metrics and tracing.',
      content: (
        <>
          <p>Prometheus is an open-source monitoring system that scrapes time-series metrics...</p>
          <p>Combine with Grafana for dashboards and Jaeger for tracing...</p>
          <p>Define SLOs and SLIs to measure reliability...</p>
        </>
      ),
    },
    {
      id: 10,
      title: 'DevSecOps in CI/CD',
      summary: 'Embed security early in your software lifecycle.',
      content: (
        <>
          <p>DevSecOps integrates security practices into DevOps pipelines...</p>
          <p>Incorporate tools like Snyk for automated vulnerability scanning...</p>
          <p>Use policy-as-code with OPA to enforce security rules...</p>
        </>
      ),
    },
  ];

  return (
    <section className="bg-gray-50 py-16">
      <div className="container mx-auto px-4">
        {/* Page Title */}
        <h2 className="text-4xl font-bold text-center text-[#D7631F] mb-8">About Us</h2>

        {/* Mission & History */}
        <div className="flex flex-col md:flex-row items-center mb-12">
          <div className="md:w-1/2">
            <img
              src="/about-image.jpg"
              alt="About FutureYX"
              className="rounded-lg shadow-lg w-full"
            />
          </div>
          <div className="md:w-1/2 mt-8 md:mt-0 md:pl-8">
            <p className="text-lg text-gray-700 mb-4">Founded in 2023, FutureYX has rapidly become a trusted partner for businesses seeking cutting-edge IT solutions.</p>
            <p className="text-lg text-gray-700 mb-4">Our mission is to deliver secure, scalable, and responsive software & mobile applications that drive growth and innovation.</p>
            <ul className="list-disc list-inside text-gray-700 mb-6">
              <li>Client-first philosophy</li>
              <li>Innovative technology integration</li>
              <li>High standards for security and quality</li>
            </ul>
          </div>
        </div>

        {/* Blog Cards without images */}
        <h3 className="text-3xl font-semibold mb-6">Latest Insights</h3>
        <div className="grid gap-6 lg:grid-cols-2">
          {blogs.map(blog => (
            <motion.div
              key={blog.id}
              className="bg-white rounded-lg overflow-hidden shadow-lg cursor-pointer p-6"
              whileHover={{ scale: 1.03 }}
              onClick={() => setSelectedBlog(blog)}
            >
              <h4 className="text-2xl font-semibold mb-2">{blog.title}</h4>
              <p className="text-gray-600 mb-4">{blog.summary}</p>
            </motion.div>
          ))}
        </div>

        {/* Modal for Blog Detail */}
        <AnimatePresence>
          {selectedBlog && (
            <motion.div
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className="bg-white rounded-lg overflow-auto max-w-3xl w-full max-h-[90vh] p-6 relative"
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
              >
                <button
                  onClick={() => setSelectedBlog(null)}
                  className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
                <h3 className="text-3xl font-bold mb-4">{selectedBlog.title}</h3>
                <div className="prose max-w-none text-gray-700 overflow-y-auto" style={{ maxHeight: '70vh' }}>
                  {selectedBlog.content}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}